"use client";

import {
  EFFECTS_DETECTION_SEGMENT_DURATION_S,
  PITCH_ANALYSIS_SEGMENT_DURATION_S,
} from "@/constants/audioQuality";
import { postAudioQualityCheck } from "@/data/audioQuality/actions/postAudioQualityCheck";
import { AudioQualityCheck } from "@/types/defs/audioQuality/audioQualityCheck";
import { ExtractedAudioSegment } from "@/types/defs/audioQuality/extractedAudioSegment";
import { audioBufferToPitchAnalysisFile } from "@/utils/audio/audioBufferToPitchAnalysisFile";
import { audioBufferToWav } from "@/utils/audio/audioBufferToWav";
import { decodeAudioFile } from "@/utils/audio/decodeAudioFile";
import { buildFormDataForAudioQualityCheck } from "@/utils/audio/quality/buildFormDataForAudioQualityCheck";
import { extractHighestEnergySegment } from "@/utils/audio/quality/extractHighestEnergySegment";
import { extractVocalOnsetSegment } from "@/utils/audio/quality/extractVocalOnsetSegment";

export const checkAudioQuality = async ({
  audioFile,
  sweetSpot,
  shouldCheckPitchAnalysis,
  signal,
}: {
  audioFile: File;
  // If sweet spot is provided in artist data we set the same value for low and high, but if not we send the pitch range as low and high.
  sweetSpot: { low: number; high: number } | null;
  shouldCheckPitchAnalysis: boolean;
  signal: AbortSignal | null;
}): Promise<AudioQualityCheck> => {
  const audioBuffer = await decodeAudioFile(audioFile);

  const effectsDetectionSegment = await extractHighestEnergySegment({
    audioBuffer,
    duration: EFFECTS_DETECTION_SEGMENT_DURATION_S,
  });

  const audioForEffectsDetection = await audioBufferToWav({
    audioBuffer: effectsDetectionSegment.audioBuffer,
    targetFormat: null,
  });

  // Pitch analysis needs a ~1 min clip, so we only build it when requested.
  let pitchAnalysisSegment: ExtractedAudioSegment | null = null;
  let audioForPitchAnalysis: File | null = null;

  if (shouldCheckPitchAnalysis) {
    pitchAnalysisSegment = await extractVocalOnsetSegment({
      audioBuffer,
      duration: PITCH_ANALYSIS_SEGMENT_DURATION_S,
    });
    audioForPitchAnalysis = await audioBufferToPitchAnalysisFile(
      pitchAnalysisSegment.audioBuffer,
      audioFile.name
    );
  }

  const formData = buildFormDataForAudioQualityCheck({
    audioForPitchAnalysis,
    audioForEffectsDetection: [audioForEffectsDetection],
    sweetSpot,
  });

  const audioQualityResult = await postAudioQualityCheck(formData, signal);

  const effectsSegment = {
    startSeconds: effectsDetectionSegment.startSeconds,
    endSeconds: effectsDetectionSegment.endSeconds,
  };
  const pitchSegment =
    pitchAnalysisSegment === null ? null : (
      {
        startSeconds: pitchAnalysisSegment.startSeconds,
        endSeconds: pitchAnalysisSegment.endSeconds,
      }
    );

  return {
    ...audioQualityResult,
    effectsSegment,
    pitchSegment,
  };
};
